import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { Dialog, ContextMenu } from "../../components/Dialogs";
import { I18n } from '~components/I18n';
import { Switch } from '~components/Input';

import { useCalcFieldSetting } from './ShiftingHooks';
import styled from "@emotion/styled";

const SwitchBtnWrapper = styled.div`
	display: flex;
	justify-content: space-between;
	margin-bottom: 8px;

	& > div {
		display: flex;
		align-items: center;
		justify-content: center;
		border-bottom: 1px solid #e0e0e0;
		width: 170px;
		padding-bottom: 8px;

		.-right {
			margin-left: 8px;
		}

		.-left {
			margin-right: 8px;
		}
	}
`;

export const ShiftingFieldContext = React.createContext({
	fieldSetting: null,
	setFieldSetting: () => {},
	fieldIdMap: {},
	fieldInfo: {},
	showBtn: true,
});

export const ShiftingFieldProvider = ({
	fieldSetting = {},
	setFieldSetting = () => {},
	fieldIdMapOptions: propsFieldIdMap = {},
	children,
	fieldInfo,
	showBtn = true,
}) => {
	const {
		calcFieldSetting,
		fieldIdMap
	} = useCalcFieldSetting(fieldSetting, propsFieldIdMap);

	return <ShiftingFieldContext.Provider value={{
		fieldSetting: calcFieldSetting,
		setFieldSetting,
		fieldIdMap,
		fieldInfo,
		showBtn,
	}}>{children}</ShiftingFieldContext.Provider>;
};

export const useFieldSetting = () => {
	const {
		fieldSetting = {},
		setFieldSetting = () => {},
		fieldIdMap,
		fieldInfo,
		showBtn,
	} = useContext(ShiftingFieldContext);

	return {
		fieldSetting,
		setFieldSetting,
		fieldIdMap,
		fieldInfo,
		showBtn,
	};
};

const DragIcon = ({ ...props }) => {
	return (
		<span className="-icon" {...props}>
			<i className="far fa-ellipsis-v" style={{ marginRight: 1 }}></i>
			<i className="far fa-ellipsis-v"></i>
		</span>
	);
};

const getFieldSettingValues = (fieldSetting) => {
	return Object.values({
		start: fieldSetting.start,
		end: fieldSetting.end,
	}).flat();
};

const List = ({ setting, droppableId, onClickCheckBox, fieldSetting }) => {
	// fieldSetting.
	const { fieldIdMap } = useFieldSetting();
	const fieldSettingArr = useMemo(() => {
		return getFieldSettingValues(fieldSetting);
	}, [fieldSetting]);

	return (
		<Droppable droppableId={droppableId} type="column">
			{(provided, snapshot) => (
				<div
					className="-column"
					ref={provided.innerRef}
					{...provided.droppableProps}
				>
					{setting?.map((item, index) => (
						<Draggable
							draggableId={item.fieldId}
							index={index}
							key={item.fieldId}
						>
							{/* {item.name} */}
							{(provided) => {
								const checked = item.checked;
								const fieldInfo = fieldIdMap[item.fieldId] || {};
								let disable = fieldInfo.disable;

								if (fieldInfo.withOut) {
									disable = !!fieldSettingArr.find(x => {
										if (x.checked) {
											return fieldInfo.withOut.indexOf(x.fieldId) !== -1;
										}
									});
								}

								return (
									<div
										className="-item"
										{...provided.draggableProps}
										ref={provided.innerRef}
									>
										<span
											className={`checkbox${
												checked ? " checked" : ""
											}${
												disable ? " disable" : ""
											}`}
											onClick={() => {
												if (disable) return;
												onClickCheckBox(
													index,
													!item.checked
												);
											}}
										/>
										<span className="-text">
											{fieldIdMap[item.fieldId]?.name}
										</span>
										<DragIcon
											{...provided.dragHandleProps}
										/>
									</div>
								);
							}}
						</Draggable>
					))}
					{provided.placeholder}
				</div>
			)}
		</Droppable>
	);
};

// #參考：https://codesandbox.io/s/teamlist-forked-fs44m2?file=/src/App.js:464-2311
export const ShiftingFieldSettingDialog = ({
	close,
	...props
}) => {
	// const [fieldSetting, setFieldSetting] = useState(propsFieldSetting);
	const {
		fieldSetting: contextFieldSetting,
		setFieldSetting: setContextFieldSetting
	} = useFieldSetting();
	const [fieldSetting, setFieldSetting] = useState(contextFieldSetting);
	const [press, setPress] = useState(false);
	const [loading, setLoading] = useState(false);
	const spanRef = useRef();

	// useEffect(() => {
	// 	setFieldSetting(contextFieldSetting);
	// }, [contextFieldSetting]);

	const onDragEnd = (result) => {
		const { destination, source, type } = result;
		if (!destination) {
			return;
		}

		if (
			destination.droppableId === source.droppableId &&
			destination.index === source.index
		) {
			return;
		}

		if (type === "pod") {
			return;
		}

		const sourceSetting = [...fieldSetting[source.droppableId]];
		const destinationSetting =
			source.droppableId === destination.droppableId
				? sourceSetting
				: [...fieldSetting[destination.droppableId]];

		const extracted = sourceSetting.splice(source.index, 1);
		destinationSetting.splice(destination.index, 0, ...extracted);

		setFieldSetting({
			...fieldSetting,
			[source.droppableId]: sourceSetting,
			[destination.droppableId]: destinationSetting,
		});
	};

	const updateCheck = (checked, key, index) => {
		const arr = [...fieldSetting[key]];
		const item = {...arr[index]};
		item.checked = checked;
		arr[index] = item;
		setFieldSetting({
			...fieldSetting,
			[key]: arr
		});
	};

	const valid = (value) => {
		const _value = getFieldSettingValues(value);
		const staffName =  !!_value.find(item => {
			if(item.fieldId === "emp.empNo") {
				return item.checked;
			}
			if (item.fieldId === "emp.enName") {
				return item.checked;
			}
			if (item.fieldId === "emp.name") {
				return item.checked;
			}
			if (item.fieldId === "emp.empNoAndName") {
				return item.checked;
			}
		});
		if (!staffName) {
			Utils.noticeError(null, OptionUtils.getLabel('h2.am.ShiftingFieldSetting', 'staffNameNameStaffEnglishName'));
			return false;
		}
		let freezeFields = 0;
		if (value.freezeStart) {
			value.start.forEach((item, index) => {
				if (item.checked) {
					freezeFields++;
				}
			});
		}
		if (value.freezeEnd) {
			value.end.forEach((item, index) => {
				if (item.checked) {
					freezeFields++;
				}
			});
		}
		if (freezeFields > 10) {
			Utils.noticeError(null, OptionUtils.getLabel('shift.am.ShiftingFieldSetting', 'theNumberOfBlockedColumnsCannot'));
			return false;
		}

		return true;
	};

	const getSwtichStr = (value) => {
		return value ? OptionUtils.getLabel('shift.am.ShiftingFieldSetting', 'open') : OptionUtils.getLabel('global.normal', 'close');
	};

	if (!fieldSetting) return null;

	return (
		<Dialog
			{...{
				title: OptionUtils.getLabel('h2.am.Shifting', 'fieldSettings'),
				className: "H2-ShiftingFieldSetting",
				height: '75%',
				onOk: () => {
					setLoading(true);
					if (valid(fieldSetting)) {
						setContextFieldSetting(fieldSetting, close);
					}
					setLoading(false);
					// else {
					//
					//
					// }
				},
				close,
				working: loading,
				...props,
			}}
		>
			<div className="-top">
				<I18n key1='h2.am.ShiftingFieldSetting' key2='pleaseSelectTheItemsYouNeed' options={{'temp1': <DragIcon />}} />
				<span className="-info">
					<i className="far fa-info-circle"></i>
					<span
						ref={spanRef}
						onMouseEnter={() => setPress(true)}
						onMouseMove={() => setPress(true)}
						onMouseLeave={() => setPress(false)}
						style={{ height: 20, width: 100 }}
					>
						{OptionUtils.getLabel('h2.am.ShiftingFieldSetting', 'illustrate')}
					</span>
					{ press ? <ContextMenu {...{
						stick: spanRef.current,
						stickOffset: { x: 80, y: -40 },
						pointer: 'left',
						pointerStyle: { top: 20 },
						close: () => {},
						className: 'H2-FieldSettingDirectDialog'
					}}>
						<div style={{ padding: '16px 24px' }}>
							<img {...{
								width: 300,
								src: 'img/field-setting-direct.png',
								alt: 'direct'
							}} />
						</div>
					</ContextMenu> : null }
				</span>
			</div>
			<SwitchBtnWrapper>
				<div>
					<div className="-left">{OptionUtils.getLabel('shift.am.ShiftingFieldSetting', 'theLeftSideFreezes')}</div>
					<Switch
						value={fieldSetting.freezeStart ? 1: 0}
						onUpdate={(v) => {
							setFieldSetting({
								...fieldSetting,
								freezeStart: v
							});
						}}
						showText={false}
					/>
					<div className="-right">
						{getSwtichStr(fieldSetting.freezeStart)}
					</div>
				</div>
				<div>
					<div className="-left">{OptionUtils.getLabel('shift.am.ShiftingFieldSetting', 'theRightSideFreezes')}</div>
					<Switch
						value={fieldSetting.freezeEnd ? 1 : 0}
						onUpdate={(v) => {
							setFieldSetting({
								...fieldSetting,
								freezeEnd: v
							});
						}}
						showText={false}
					/>
					<div className="-right">
						{getSwtichStr(fieldSetting.freezeEnd)}
					</div>
				</div>
			</SwitchBtnWrapper>
			<DragDropContext onDragEnd={onDragEnd} onDragStart={() => {}}>
				<Droppable
					droppableId="allPods"
					direction="horizontal"
					type="pod"
				>
					{(provided) => (
						<div
							className="-drag-wrapper"
							{...provided.draggableProps}
							ref={provided.innerRef}
						>
							{['start', 'end'].map((key, index) => {
								const setting = fieldSetting[key];
								return (
									<React.Fragment key={key}>
										<List
											setting={setting}
											droppableId={key}
											fieldSetting={fieldSetting}
											onClickCheckBox={(
												index,
												checked
											) => {
												updateCheck(checked, key, index);
											}}
										/>
										{!index ? (
											<div className="-background">
												<div className="text-center" style={{ color: '#EA475B' }}>
													* {OptionUtils.getLabel('shift.am.ShiftingFieldSetting', 'theNumberOfBlockedColumnsCannot')}
												</div>
												<img  src="img/field-setting-center.png" alt="background" />
											</div>
										) : null}
									</React.Fragment>
								);
							})}
							{provided.placeholder}
						</div>
					)}
				</Droppable>
			</DragDropContext>
		</Dialog>
	);
};
